// COACHING PERSONALITY — the two things the surface needs from the UI language.
//
// 1. HOW A DATE READS. Konzept 4.6: the released card renders for every viewer
//    in THAT viewer's language, and no translated text is ever stored on a
//    result. The as-of date is the one value that is not a bundle entry, so it
//    is formatted here, in the viewer's language, month and year only ("As of
//    August 2026"). Rendering an English month inside a German sentence would
//    be the one untranslated word on an otherwise translated card.
//
//    The sentence around it is a TEMPLATE key ("As of {date}"), and the matcher
//    substitutes what it captured verbatim (registerStaticTranslations.js), so
//    a localised month survives the substitution unchanged.
//
// 2. WHICH WAY THE BLOCK RUNS. The platform has no RTL support at all: no `dir`
//    anywhere, no logical properties. Turning the whole app around is its own
//    project. This block therefore declares its OWN direction, additively, and
//    changes nothing outside itself. Two things are physical rather than
//    directional and are mirrored by hand where they are used: the marker's
//    `left` offset on a spectrum, and the text alignment of the three zone
//    labels under it.
import { normalizeLang } from "../../../translations/registerStaticTranslations";

// Languages the platform ships that are written right to left. Only Arabic is
// in the sixteen; the set is named rather than hard-coded to one string so a
// future addition (he, fa, ur) is a one-word change.
const RTL_LANGUAGES = new Set(["ar", "he", "fa", "ur"]);

export const isRtlLanguage = (language) =>
  RTL_LANGUAGES.has(String(language || "").toLowerCase().split("-")[0]);

/**
 * "August 2026" in the viewer's language, or "" when there is no date.
 *
 * Returns the empty string rather than throwing or printing "Invalid Date":
 * a missing date must render nothing, like every other absence in this feature.
 */
export const monthYear = (value, language) => {
  if (!value) return "";
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  const options = { month: "long", year: "numeric" };
  try {
    return new Intl.DateTimeFormat(normalizeLang(language), options).format(date);
  } catch (error) {
    return new Intl.DateTimeFormat("en", options).format(date);
  }
};

export default monthYear;
